import {
  combineDateAndTime,
  HoursOff,
  useGetUsersQuery,
  useGetUsersWorkingTodayQuery,
  UsersPermissionsUser
} from "@mc-intranet/shared";
import { Container, Grid, List, ListItem, ListItemText } from "@mui/material";
import { useAtomValue } from "jotai";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { authStorageAtom } from "../../atoms";
import { useGraphQLClient } from "../../cache";
import TodayCard from "../../components/today/Card";
import Loading from "../../components/ui/Loading";

interface UsersWithHoursOff extends UsersPermissionsUser {
  hoursOff: HoursOff[];
}
type UsersStatus = {
  working: UsersPermissionsUser[];
  onVacation: UsersPermissionsUser[];
  withHoursOff: UsersWithHoursOff[];
};

const Today = () => {
  const authStorage = useAtomValue(authStorageAtom);
  const { user } = authStorage;

  const [usersStatus, setUsersStatus] = useState<UsersStatus>({
    working: [],
    onVacation: [],
    withHoursOff: []
  });
  const [usersNotPunced, setUsersNotPunced] = useState<string[]>([]);

  const { client } = useGraphQLClient();

  const { data: usersData, isLoading: usersLoading } = useGetUsersQuery(
    client,
    {},
    {
      enabled: !!user,
      onError: (error: any) => {
        toast.error(error?.response?.error.message, {
          position: toast.POSITION.TOP_CENTER
        });
      }
    }
  );

  const { data: todayData, isLoading: todayLoading } =
    useGetUsersWorkingTodayQuery(
      client,

      {
        startDate: moment().format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD")
      },
      {
        enabled: !!user,
        onSuccess: (data) => {
          // console.log("getUsersWorkingTodayQuery onCompleted data", data);

          const working = data.working
            ?.map((item) => item?.user)
            .sort((a, b) =>
              (a?.firstName || "") >
              (b?.firstName || "")
                ? 1
                : (b?.firstName || "") >
                  (a?.firstName || "")
                ? -1
                : 0
            );
          // console.log("working", working);

          const onVacation = data.onVacation
            ?.map((item) => item?.user)
            .sort((a, b) =>
              (a?.firstName || "") >
              (b?.firstName || "")
                ? 1
                : (b?.firstName || "") >
                  (a?.firstName || "")
                ? -1
                : 0
            );
          // console.log("onVacation", onVacation);

          const withHoursOff = data.withHoursOff
            ?.map((item) => {
              return {
                ...item?.user,
                hoursOff: item?.hoursOff
              };
            })
            .sort((a, b) =>
              (a?.firstName || "") >
              (b?.firstName || "")
                ? 1
                : (b?.firstName || "") >
                  (a?.firstName || "")
                ? -1
                : 0
            );
          // console.log("withHoursOff", withHoursOff);

          setUsersStatus({
            working: working as UsersPermissionsUser[],
            onVacation: onVacation as UsersPermissionsUser[],
            withHoursOff: withHoursOff as UsersWithHoursOff[]
          });
        },
        onError: (error: any) => {
          toast.error(error?.response?.error.message, {
            position: toast.POSITION.TOP_CENTER
          });
        }
      }
    );

  useEffect(() => {
    if (usersData && todayData) {
      const presentUserIds = new Set([
        ...usersStatus.working,
        ...usersStatus.onVacation,
        ...usersStatus.withHoursOff
      ].map((item) => item?.documentId));

      const presentUsers = [
        ...usersStatus.working,
        ...usersStatus.onVacation,
        ...usersStatus.withHoursOff
      ].map(
        (item) => `${item?.firstName} ${item?.lastName}`
      );

      const difference =
        usersData.usersPermissionsUsers
          ?.filter((item) => {
            const mustPunch = item?.mustPunch;
            return mustPunch !== false && !presentUserIds.has(item?.documentId as string);
          })
          .map(
            (item) => `${item?.firstName} ${item?.lastName}`
          ) || [];
      // console.log("difference :>> ", difference);

      setUsersNotPunced(difference);
    }
  }, [
    todayData,
    usersData,
    usersStatus.onVacation,
    usersStatus.withHoursOff,
    usersStatus.working
  ]);
  // console.log("usersData ", usersData);
  // console.log("todayData ", todayData);
  // console.log("usersStatus ", usersStatus);

  const isLoggedIn = !user || usersLoading || todayLoading;

  return (
    <Container
      maxWidth="xl"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        minHeight: {
          xs: "calc(100vh - 56px - 56px)",
          md: "calc(100vh - 69px - 56px)"
        },
        position: "relative",
        pb: 3
      }}
    >
      {isLoggedIn || todayLoading ? (
        <Loading />
      ) : (
        <Grid container spacing={2} my={4}>
          <Grid item className="gutter-row" xs={12} md={3}>
            <TodayCard title="Presenti">
              <List dense={true}>
                {usersStatus.working.map((item) => {
                  return (
                    <ListItem key={item.documentId}>
                      <ListItemText
                        primary={`${item?.firstName} ${item?.lastName}`}
                      />
                    </ListItem>
                  );
                })}
                {usersStatus.working.length === 0 && (
                  <ListItem>
                    <ListItemText primary="Nessuno" />
                  </ListItem>
                )}
              </List>
            </TodayCard>
          </Grid>
          <Grid item className="gutter-row" xs={12} md={3}>
            <TodayCard title="In Ferie">
              <List dense={true}>
                {usersStatus.onVacation.map((item) => {
                  return (
                    <ListItem key={item.documentId}>
                      <ListItemText
                        primary={`${item?.firstName} ${item?.lastName}`}
                      />
                    </ListItem>
                  );
                })}
                {usersStatus.onVacation.length === 0 && (
                  <ListItem>
                    <ListItemText primary="Nessuno" />
                  </ListItem>
                )}
              </List>
            </TodayCard>
          </Grid>
          <Grid item className="gutter-row" xs={12} md={3}>
            <TodayCard title="In permesso">
              <List dense={true}>
                {usersStatus.withHoursOff.map((item) => {
                  return (
                    <ListItem key={item.documentId}>
                      <ListItemText
                        primary={
                          <div>
                            <div>{`${item?.firstName} ${item?.lastName}`}</div>
                            <div>
                              {item.hoursOff.map((off) => {
                                const _today = moment().format("YYYY-MM-DD");
                                return `(${combineDateAndTime(
                                  _today,
                                  off?.from,
                                  "HH:mm"
                                )} - ${combineDateAndTime(
                                  _today,
                                  off?.to,
                                  "HH:mm"
                                )})`;
                              })}
                            </div>
                          </div>
                        }
                      />
                    </ListItem>
                  );
                })}
                {usersStatus.withHoursOff.length === 0 && (
                  <ListItem>
                    <ListItemText primary="Nessuno" />
                  </ListItem>
                )}
              </List>
            </TodayCard>
          </Grid>
          <Grid item className="gutter-row" xs={12} md={3}>
            <TodayCard title="Non timbrato">
              <List dense={true}>
                {usersNotPunced.map((item) => {
                  return (
                    <ListItem key={item}>
                      <ListItemText primary={item} />
                    </ListItem>
                  );
                })}
                {usersNotPunced.length === 0 && (
                  <ListItem>
                    <ListItemText primary="Nessuno" />
                  </ListItem>
                )}
              </List>
            </TodayCard>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default Today;
